const clamp = value => Math.max(0, Math.min(1, value));
export const isPaperEntrance = value => value === 'paper-unfold' || value?.in === 'paper-unfold';
export const paperDuration = (fps, frames) => Math.min(2.6, frames / fps * .65);

// A textured triangular mesh unfolds from the centre. Corners lead the pull;
// the interior follows, with real perspective and diminishing crease lighting.
export function paperVertex(u, v, progress, width, height) {
  const corner = Math.pow(Math.abs(u - .5) * 2 * Math.abs(v - .5) * 2, .5);
  const p = clamp((progress - (1 - corner) * .2) / (1 - (1 - corner) * .2));
  const ease = 1 - Math.pow(1 - p, 3);
  const fold = (1 - ease);
  const wave = Math.sin(u * Math.PI * 6 + v * 3) * Math.cos(v * Math.PI * 5 - u * 4);
  const x = (u - .5) * width * (.2 + .8 * ease) + wave * 32 * fold;
  const y = (v - .5) * height * (.23 + .77 * ease) + Math.sin(u * 17 + v * 21) * 27 * fold;
  const z = (wave * 70 + Math.sin((u + v) * 13) * 30) * fold;
  const perspective = 1200 / (1200 - z);
  return {x:width / 2 + x * perspective,y:height / 2 + y * perspective,z};
}

function triangle(ctx, image, uv, points) {
  const [a,b,c] = points, [s,t,r] = uv;
  const determinant = s.x * (t.y-r.y) + t.x * (r.y-s.y) + r.x * (s.y-t.y);
  if (Math.abs(determinant) < .0001) return;
  const affine = axis => [
    (a[axis]*(t.y-r.y)+b[axis]*(r.y-s.y)+c[axis]*(s.y-t.y))/determinant,
    (a[axis]*(r.x-t.x)+b[axis]*(s.x-r.x)+c[axis]*(t.x-s.x))/determinant,
    (a[axis]*(t.x*r.y-r.x*t.y)+b[axis]*(r.x*s.y-s.x*r.y)+c[axis]*(s.x*t.y-t.x*s.y))/determinant,
  ];
  const x = affine('x'), y = affine('y');
  ctx.save();ctx.beginPath();ctx.moveTo(a.x,a.y);ctx.lineTo(b.x,b.y);ctx.lineTo(c.x,c.y);ctx.closePath();ctx.clip();
  ctx.transform(x[0],y[0],x[1],y[1],x[2],y[2]);ctx.drawImage(image,0,0);ctx.restore();
}

export function preparePaper(element, frame, fps, frames) {
  if (element.tagName !== 'IMG' || !element.complete || !element.naturalWidth) return null;
  const doc = element.ownerDocument, canvas = doc.createElement('canvas');
  const computed = doc.defaultView.getComputedStyle(element);
  const width = Math.max(1,Math.round(parseFloat(computed.width)||element.width));
  const height = Math.max(1,Math.round(parseFloat(computed.height)||element.height));
  for (const attr of element.attributes) if (attr.name !== 'src' && attr.name !== 'srcset') canvas.setAttribute(attr.name,attr.value);
  canvas.width=width;canvas.height=height;
  const ctx=canvas.getContext('2d'), p=clamp(frame/fps/paperDuration(fps,frames));
  ctx.imageSmoothingEnabled=true;ctx.imageSmoothingQuality='high';
  const cols=24, rows=16, vertices=[];
  for(let j=0;j<=rows;j++)for(let i=0;i<=cols;i++)vertices.push(paperVertex(i/cols,j/rows,p,width,height));
  const paint=(ids,uv)=>{
    const points=ids.map(id=>vertices[id]);triangle(ctx,element,uv,points);
    const [a,b,c]=points;const ux=b.x-a.x,uy=b.y-a.y,uz=b.z-a.z,vx=c.x-a.x,vy=c.y-a.y,vz=c.z-a.z;
    const nx=uy*vz-uz*vy,ny=uz*vx-ux*vz,nz=ux*vy-uy*vx;
    const length=Math.hypot(nx,ny,nz)||1;
    const light=(nx*.3-ny*.5+nz*.8)/length;
    const shade=clamp((.8-light)*.7)*(1-p);
    ctx.fillStyle=`rgba(12,9,4,${shade})`;ctx.beginPath();ctx.moveTo(a.x,a.y);ctx.lineTo(b.x,b.y);ctx.lineTo(c.x,c.y);ctx.closePath();ctx.fill();
  };
  for(let j=0;j<rows;j++)for(let i=0;i<cols;i++){
    const a=j*(cols+1)+i,b=a+1,c=a+cols+1,d=c+1;
    const uv=(x,y)=>({x:x/cols*element.naturalWidth,y:y/rows*element.naturalHeight});
    paint([a,b,d],[uv(i,j),uv(i+1,j),uv(i+1,j+1)]);paint([a,d,c],[uv(i,j),uv(i+1,j+1),uv(i,j+1)]);
  }
  canvas.style.filter=`drop-shadow(0 ${12*(1-p)}px ${16*(1-p)}px rgba(0,0,0,${.3*(1-p)}))`;
  // A decoded raster snapshot makes the procedural frame reliable in both
  // Chromium's screenshot renderer and the interactive Player.
  const rendered=doc.createElement('img');
  for(const attr of canvas.attributes) rendered.setAttribute(attr.name,attr.value);
  rendered.removeAttribute('width');rendered.removeAttribute('height');
  rendered.src=canvas.toDataURL('image/png');
  element.replaceWith(rendered);
  return {ready:rendered.decode(),restore(){rendered.replaceWith(element);}};
}
